import type { PillTone } from '@/components/ui/pill';
import type { QuotaRow, QuotaStatus, QuotaWeek } from '@/api/types';

/**
 * Display rules for the Message quotas page. The server grades each week
 * (under / hit / over / none); this adds the one thing it can't know: a week
 * still in progress that is under quota is "to go", not a miss.
 */

/** What a row shows for one week. `open` is a week still in progress and short. */
export type QuotaView = 'none' | 'open' | 'under' | 'hit' | 'over';

export const QUOTA_VIEWS: readonly QuotaView[] = ['under', 'open', 'hit', 'over', 'none'];

export function quotaView(status: QuotaStatus, isCurrentWeek: boolean): QuotaView {
  return status === 'under' && isCurrentWeek ? 'open' : status;
}

export const QUOTA_VIEW_LABEL: Record<QuotaView, string> = {
  under: 'Under',
  open: 'In progress',
  hit: 'Hit',
  over: 'Over',
  none: 'No quota',
};

export const QUOTA_VIEW_TONE: Record<QuotaView, PillTone> = {
  under: 'failed',
  open: 'pending',
  hit: 'paid',
  over: 'verified',
  none: 'muted',
};

/** Pill text with the gap spelled out: "Under by 20", "20 to go", "Over by 34". */
export function quotaViewText(view: QuotaView, messages: number, quota: number | null): string {
  if (quota === null || view === 'none') return QUOTA_VIEW_LABEL.none;
  const gap = Math.abs(messages - quota).toLocaleString();
  switch (view) {
    case 'under':
      return `Under by ${gap}`;
    case 'open':
      return `${gap} to go`;
    case 'over':
      return `Over by ${gap}`;
    default:
      return 'Hit quota';
  }
}

/** Share of quota done, as a whole percent. Null with no quota or a zero one. */
export function quotaPercent(messages: number, quota: number | null): number | null {
  if (quota === null || quota <= 0) return null;
  return Math.round((messages / quota) * 100);
}

/** The row's entry for `weekStart`; a zeroed one if the week isn't loaded. */
export function weekOf(row: QuotaRow, weekStart: string): QuotaWeek {
  return (
    row.weeks.find((w) => w.weekStart === weekStart) ?? {
      weekStart,
      messages: 0,
      status: row.weeklyMessageQuota === null ? 'none' : 'under',
    }
  );
}

export interface QuotaSummary {
  counts: Record<QuotaView, number>;
  messages: number;
  /** Sum of quotas across outreachers who have one. */
  quota: number;
}

export function summarizeQuotaWeek(rows: readonly QuotaRow[], weekStart: string, isCurrentWeek: boolean): QuotaSummary {
  const counts: Record<QuotaView, number> = { under: 0, open: 0, hit: 0, over: 0, none: 0 };
  let messages = 0;
  let quota = 0;
  for (const r of rows) {
    const w = weekOf(r, weekStart);
    counts[quotaView(w.status, isCurrentWeek)] += 1;
    messages += w.messages;
    quota += r.weeklyMessageQuota ?? 0;
  }
  return { counts, messages, quota };
}

/**
 * Furthest behind first: by share of quota ascending, then name. Outreachers
 * with no quota go last, busiest first.
 */
export function sortQuotaRows(rows: readonly QuotaRow[], weekStart: string): QuotaRow[] {
  const share = (r: QuotaRow): number | null => {
    const q = r.weeklyMessageQuota;
    if (q === null) return null;
    const m = weekOf(r, weekStart).messages;
    return q > 0 ? m / q : m > 0 ? Number.POSITIVE_INFINITY : 1;
  };
  return [...rows].sort((a, b) => {
    const sa = share(a);
    const sb = share(b);
    if (sa === null && sb === null) {
      return weekOf(b, weekStart).messages - weekOf(a, weekStart).messages || a.name.localeCompare(b.name);
    }
    if (sa === null) return 1;
    if (sb === null) return -1;
    return sa - sb || a.name.localeCompare(b.name);
  });
}

/**
 * Parse what was typed into a quota box. '' clears the quota (null); anything
 * that isn't a whole number from 0 to 100,000 is rejected (undefined).
 */
export function parseQuotaInput(raw: string): number | null | undefined {
  const t = raw.trim().replace(/,/g, '');
  if (t === '') return null;
  if (!/^\d+$/.test(t)) return undefined;
  const n = Number(t);
  return n <= 100000 ? n : undefined;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "5 – 11 Oct" for a Monday week start (UTC). */
export function weekRangeLabel(weekStart: string): string {
  const start = new Date(`${weekStart}T00:00:00.000Z`);
  if (Number.isNaN(start.getTime())) return weekStart;
  const end = new Date(start.getTime() + 6 * 86_400_000);
  const day = (d: Date) => d.getUTCDate();
  const month = (d: Date) => MONTHS[d.getUTCMonth()];
  return start.getUTCMonth() === end.getUTCMonth()
    ? `${day(start)} – ${day(end)} ${month(end)}`
    : `${day(start)} ${month(start)} – ${day(end)} ${month(end)}`;
}

// ── The outreacher's own view ──────────────────────────────────────────────

export interface WeeklyMinimumCopy {
  /** below the minimum, exactly on it, or past it. */
  state: 'below' | 'met' | 'past';
  headline: string;
  detail: string;
  /** Share of the minimum done, 0 to 100, for the bar. */
  percent: number;
}

/**
 * What an outreacher reads about their own week. The quota is a floor, never
 * a cap: below it the copy counts down, and at or past it the copy says so
 * and encourages going further. Nothing here mentions pay.
 */
export function weeklyMinimumCopy(messages: number, minimum: number): WeeklyMinimumCopy {
  const percent = minimum > 0 ? Math.min(Math.round((messages / minimum) * 100), 100) : 100;
  if (messages < minimum) {
    const left = (minimum - messages).toLocaleString();
    return {
      state: 'below',
      headline: `${left} to go to hit your minimum`,
      detail: `${minimum.toLocaleString()} is the least we expect in a week. Go past it and that's even better.`,
      percent,
    };
  }
  if (messages === minimum) {
    return {
      state: 'met',
      headline: 'Minimum hit',
      detail: "That's the minimum, not the limit. Every creator you message past this is even better.",
      percent,
    };
  }
  return {
    state: 'past',
    headline: `${(messages - minimum).toLocaleString()} past your minimum`,
    detail: 'This is exactly what we like to see. Keep going.',
    percent,
  };
}
